import React, { useEffect, useState } from 'react';
import '../../styles/animations.css';

/**
 * AlertBanner Component
 *
 * Displays error, success, or info messages with optional actions.
 * Supports auto-close and slide-down animation.
 *
 * @param {string} message - Message to display
 * @param {string} type - 'error' | 'success' | 'info' (default: 'error')
 * @param {function} onRetry - Optional retry callback
 * @param {function} onClose - Optional close callback
 * @param {boolean} autoClose - Auto-close after 3 seconds (default: false for error, true for success)
 */
const AlertBanner = ({ message, type = 'error', onRetry, onClose, autoClose }) => {
  const [isVisible, setIsVisible] = useState(true);
  const [isExiting, setIsExiting] = useState(false);

  // Determine auto-close behavior
  const shouldAutoClose = autoClose !== undefined ? autoClose : type === 'success';

  useEffect(() => {
    if (message && shouldAutoClose) {
      const timer = setTimeout(() => {
        handleClose();
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [message, shouldAutoClose]);

  const handleClose = () => {
    setIsExiting(true);
    setTimeout(() => {
      setIsVisible(false);
      if (onClose) onClose();
    }, 200); // Match fade-out duration
  };

  if (!message || !isVisible) return null;

  // Style based on type
  const styles = {
    error: {
      backgroundColor: '#fee',
      border: '1px solid #fcc',
      borderLeft: '4px solid #f44',
      color: '#c33'
    },
    success: {
      backgroundColor: '#efe',
      border: '1px solid #cfc',
      borderLeft: '4px solid #4c4',
      color: '#3a3'
    },
    info: {
      backgroundColor: '#eef',
      border: '1px solid #ccf',
      borderLeft: '4px solid #44f',
      color: '#33a'
    }
  };

  const currentStyle = styles[type] || styles.error;
  const animationClass = isExiting ? 'banner-exit' : 'banner-enter';

  return (
    <div
      className={animationClass}
      style={{
        marginBottom: '16px',
        padding: '12px 16px',
        borderRadius: '4px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        ...currentStyle
      }}
    >
      <div style={{ flex: 1 }}>
        <div style={{ marginBottom: onRetry || onClose ? '8px' : 0, fontWeight: 500 }}>
          {message}
        </div>
        {(onRetry || onClose) && (
          <div style={{ display: 'flex', gap: '8px' }}>
            {onRetry && (
              <button
                type="button"
                onClick={onRetry}
                className="btn-primary"
                style={{ padding: '6px 10px', fontSize: '13px' }}
              >
                再試行
              </button>
            )}
            {onClose && !shouldAutoClose && (
              <button
                type="button"
                onClick={handleClose}
                className="btn-secondary"
                style={{ padding: '6px 10px', fontSize: '13px' }}
              >
                閉じる
              </button>
            )}
          </div>
        )}
      </div>
      {shouldAutoClose && (
        <button
          type="button"
          onClick={handleClose}
          style={{
            background: 'none',
            border: 'none',
            fontSize: '18px',
            cursor: 'pointer',
            padding: '0 0 0 12px',
            color: 'inherit',
            opacity: 0.6
          }}
          aria-label="閉じる"
        >
          ×
        </button>
      )}
    </div>
  );
};

export default AlertBanner;


